import { Controller } from '@hotwired/stimulus';

/**
 * cr-anchor-scroll — un lien vers une section du CR (`#section-…`) atterrit sur la
 * section, pas en haut de la page (revue de l'agent, SEC-1, plan 2026-10-03, S10).
 *
 * POURQUOI IL EXISTE. La page du CR ne défile pas dans la fenêtre : elle défile dans
 * `<main class="overflow-y-auto">` (`layouts/application.html.erb`). Turbo, lui,
 * restaure et fait défiler `window`. Sur une visite de restauration (bouton retour),
 * Turbo remet (0,0) et ne va jamais chercher l'ancre ; avec un aperçu en cache,
 * l'ancre est appliquée à l'aperçu, puis perdue quand la vraie page le remplace.
 * Résultat : le lien « voir la section » posait l'architecte en haut du CR.
 *
 * POURQUOI `connect()` ET `requestAnimationFrame`. Monté sur la racine de
 * `meeting_reports/show.html.erb`, il se connecte à chaque rendu de la vraie page.
 * Le défilement attend le cadre suivant, quand la page est posée et que Turbo a
 * fini avec `window`. `scrollIntoView` fait défiler l'ancêtre qui défile vraiment
 * (`<main>`), ce que le défilement natif vers l'ancre ne garantit pas ici.
 *
 * POURQUOI PAS PENDANT L'APERÇU. Sous `<html data-turbo-preview>`, la page affichée
 * est la copie du cache : elle sera remplacée, et la vraie page, en se connectant,
 * défilera elle-même.
 *
 * Un hash qui ne désigne rien ne fait rien. Pas de listener global : rien à retirer
 * en `disconnect()`. Le cadre en attente est annulé si l'élément part avant.
 *
 *   data-controller="cr-anchor-scroll"
 */
export default class extends Controller {
  connect() {
    if (document.documentElement.hasAttribute('data-turbo-preview')) return;

    const id = this.#anchorId();
    if (!id) return;

    const target = document.getElementById(id);
    if (!target) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      target.scrollIntoView({ block: 'start' });
    });
  }

  disconnect() {
    if (this.frame && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.frame);
    }
    this.frame = null;
  }

  // Le hash sans son `#`, décodé (un id accentué arrive encodé dans l'URL).
  #anchorId() {
    const hash = window.location.hash.slice(1);
    if (!hash) return null;

    try {
      return decodeURIComponent(hash);
    } catch {
      return hash;
    }
  }
}
